// ════════════════════════════════════════════════════════════════════════
// Button.tsx — Componente botón reutilizable con variantes
// ════════════════════════════════════════════════════════════════════════
// CONCEPTO: Props tipadas con TypeScript
// Las props son los "parámetros" que el padre pasa al componente hijo.
// Tiparlas con una interface hace que el editor:
//   1. Autocomplete los nombres de props
//   2. Muestre error si pasan una prop incorrecta
//   3. Indique cuáles son opcionales (?) y cuáles requeridas
//
// CONCEPTO: ComponentPropsWithoutRef<'button'>
// Utility type de React que incluye TODAS las props nativas del <button>
// (onClick, disabled, type, aria-*, etc.) en nuestra interface custom.
// Así el usuario puede pasar cualquier prop nativa sin que nosotros
// la declaremos manualmente una por una.
// ════════════════════════════════════════════════════════════════════════

import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import styles from './Button.module.css';

// ── Tipos de variantes ────────────────────────────────────────────────
// String literal union — solo estos valores son válidos para variant y size.
type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

// ── Interface de props ────────────────────────────────────────────────
// Extiende ComponentPropsWithoutRef<'button'> para heredar todas las
// props HTML nativas del elemento button (onClick, disabled, type, etc.)
interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  variant?: ButtonVariant;       // Estilo visual — default: 'primary'
  size?: ButtonSize;             // Tamaño — default: 'md'
  isLoading?: boolean;           // Muestra spinner y deshabilita el botón
  children: ReactNode;           // Contenido del botón (texto, ícono, etc.)
  href?: string;                 // Si se provee, renderiza <a> en vez de <button>
  icon?: ReactNode;              // Ícono opcional (SVG) al final del texto —
                                  // usar íconos de línea simple (currentColor,
                                  // sin relleno), nunca emoji: se ven distinto
                                  // en cada sistema operativo/navegador y leen
                                  // como poco profesionales en un botón de CTA.
}

// ── Componente ────────────────────────────────────────────────────────
// Destructuring de props con valores default.
// "..." rest — captura cualquier prop nativa no declarada explícitamente
// (ej: onClick, aria-label) para pasarla al elemento subyacente.
export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  children,
  href,
  icon,
  className = '',
  disabled,
  ...rest
}: ButtonProps) {
  // Construir el className dinámicamente.
  // [styles.xxx] — usa corchetes para acceder con variable a CSS Modules.
  // filter(Boolean) — elimina valores undefined/null/''/false del array.
  const classNames = [
    styles.btn,
    styles[variant],
    size !== 'md' ? styles[size] : '',
    isLoading ? styles.loading : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const isDisabled = disabled ?? isLoading;

  // Si se provee href, renderizar como enlace <a> con estilos de botón.
  // Patrón: "Button as Link" — mismos estilos, diferente elemento HTML.
  if (href) {
    return (
      <a href={href} className={classNames} aria-disabled={isDisabled}>
        {isLoading && <span className={styles.spinner} aria-hidden="true" />}
        {children}
        {!isLoading && icon && <span className={styles.icon} aria-hidden="true">{icon}</span>}
      </a>
    );
  }

  return (
    // {...rest} — spread de props nativas: onClick, type, aria-label, etc.
    <button className={classNames} disabled={isDisabled} {...rest}>
      {/* aria-hidden en el spinner lo oculta de lectores de pantalla */}
      {isLoading && <span className={styles.spinner} aria-hidden="true" />}
      {children}
      {!isLoading && icon && <span className={styles.icon} aria-hidden="true">{icon}</span>}
    </button>
  );
}
